import React, { createContext, useContext, useEffect, useState } from 'react'
import { defaultLocale, loadMessages, locales } from '../i18n'
import { useNavigate, useLocation } from 'react-router-dom'
import { localStorage } from '@services/local-storage.service'
import { StorageKeys } from '@constants/storage-keys.constant'
import { User } from '@interfaces/user.interface'

interface LanguageContextType {
  currentLanguage: string
  availableLanguages: Record<string, string>
  changeLanguage: (lang: string) => void
  getAlternateUrls: () => Record<string, string>
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined)

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState<string>(defaultLocale)
  const navigate = useNavigate()
  const location = useLocation()

  // Get user's preferred language from localStorage
  const getUserPreferredLanguage = (): string | null => {
    try {
      const user = localStorage.getItem<User>(StorageKeys.USER)
      return user?.settings?.language || null
    } catch {
      return null
    }
  }

  // Initialize language from URL or browser preferences
  useEffect(() => {
    const initializeLanguage = async () => {
      // Check if URL has language prefix
      const pathSegments = location.pathname.split('/')
      const langFromUrl = pathSegments[1]
      
      if (langFromUrl && Object.keys(locales).includes(langFromUrl)) {
        // Language from URL path - this takes precedence
        setCurrentLanguage(langFromUrl)
        await loadMessages(langFromUrl)
      } else {
        // Determine preferred language: user preference > browser language > default
        const userPreferredLang = getUserPreferredLanguage()
        const browserLang = navigator.language.split('-')[0]
        const preferredLang = 
          (userPreferredLang && Object.keys(locales).includes(userPreferredLang)) ? userPreferredLang :
          (Object.keys(locales).includes(browserLang)) ? browserLang :
          defaultLocale
        
        setCurrentLanguage(preferredLang)
        await loadMessages(preferredLang)
        
        // Redirect to language-prefixed URL with the preferred language
        if (location.pathname !== '/') {
          navigate(`/${preferredLang}${location.pathname}`, { replace: true })
        } else {
          navigate(`/${preferredLang}`, { replace: true })
        }
      }
    }

    initializeLanguage()
  }, [])

  // Change language and update URL
  const changeLanguage = async (lang: string) => {
    if (Object.keys(locales).includes(lang) && lang !== currentLanguage) {
      await loadMessages(lang)
      setCurrentLanguage(lang)
      
      // Update URL with new language prefix
      const pathSegments = location.pathname.split('/')
      if (pathSegments[1] && Object.keys(locales).includes(pathSegments[1])) {
        // Replace existing language prefix
        pathSegments[1] = lang
        navigate(pathSegments.join('/') + location.search + location.hash, { replace: true })
      } else {
        // Add language prefix
        navigate(`/${lang}${location.pathname}${location.search}${location.hash}`, { replace: true })
      }
    }
  }

  // Generate alternate URLs for all available languages
  const getAlternateUrls = (): Record<string, string> => {
    const currentUrl = new URL(window.location.href)
    const pathSegments = currentUrl.pathname.split('/')
    const alternateUrls: Record<string, string> = {}

    Object.keys(locales).forEach(lang => {
      if (pathSegments[1] && Object.keys(locales).includes(pathSegments[1])) {
        // Replace existing language prefix
        pathSegments[1] = lang
        alternateUrls[lang] = `${currentUrl.origin}${pathSegments.join('/')}${currentUrl.search}${currentUrl.hash}`
      } else {
        // Add language prefix
        alternateUrls[lang] = `${currentUrl.origin}/${lang}${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`
      }
    })

    return alternateUrls
  }

  return (
    <LanguageContext.Provider 
      value={{ 
        currentLanguage, 
        availableLanguages: locales, 
        changeLanguage,
        getAlternateUrls,
      }}
    >
      {children}
    </LanguageContext.Provider>
  )
}

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext)
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider')
  }
  return context
} 